import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { appCache } from '../lib/cache';

const { FiStar, FiArrowLeft, FiShoppingCart, FiZap, FiExternalLink, FiCalendar, FiTag } = FiIcons;

const StarDisplay = ({ value }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <SafeIcon 
        key={s} 
        icon={FiStar} 
        className={`text-xl ${s <= Math.round(value) ? 'text-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.7)]' : 'text-white/15'}`} 
        style={{ fill: s <= Math.round(value) ? '#facc15' : 'none' }} 
      />
    ))}
  </div>
);

const RelatedCard = ({ review }) => (
  <Link 
    to={`/reviews/${review.slug || review.id}`} 
    className="group flex gap-3 p-3 rounded-xl bg-[#0c0118] border border-[#2a1f42] hover:border-purple-500/30 transition-all"
  >
    {review.image_url ? (
      <img src={review.image_url} alt={review.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
    ) : (
      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white font-bold text-xl">
        {review.title.charAt(0).toUpperCase()}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-white font-semibold text-sm line-clamp-2 group-hover:text-purple-300 transition-colors">
        {review.title}
      </p>
      <div className="flex items-center gap-1 mt-1">
        {[1, 2, 3, 4, 5].map(s => (
          <SafeIcon key={s} icon={FiStar} className={`text-xs ${s <= Math.round(review.rating) ? 'text-yellow-400' : 'text-white/15'}`} style={{ fill: s <= Math.round(review.rating) ? '#facc15' : 'none' }} />
        ))}
        <span className="text-white/30 text-xs ml-1">{review.rating}</span>
      </div>
    </div>
  </Link>
);

const ReviewDetail = () => {
  const { slug } = useParams();
  const [review, setReview] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      setNotFound(false);

      // Check cache first
      if (appCache.reviewDetails[slug]) {
        setReview(appCache.reviewDetails[slug].data);
        setRelated(appCache.reviewDetails[slug].related);
        setLoading(false);
        return;
      }

      try {
        // Try by slug first
        let { data, error } = await supabase
          .from('tool_reviews_1776536215359')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .maybeSingle();

        // If not found by slug, try by id
        if (!data) {
          const res = await supabase
            .from('tool_reviews_1776536215359')
            .select('*')
            .eq('id', slug)
            .eq('status', 'published')
            .maybeSingle();
          data = res.data;
        }

        if (!data) {
          setNotFound(true);
          return;
        }

        setReview(data);

        // Fetch related reviews
        const { data: rel } = await supabase
          .from('tool_reviews_1776536215359')
          .select('id, title, slug, rating, image_url, category')
          .eq('status', 'published')
          .neq('id', data.id)
          .limit(4);

        setRelated(rel || []);

        // Cache it!
        appCache.reviewDetails[slug] = { data, related: rel || [] };

      } catch (e) {
        console.error('ReviewDetail fetch error:', e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound || !review) return (
    <div className="text-center py-32 px-4">
      <div className="w-20 h-20 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <SafeIcon icon={FiStar} className="text-3xl text-purple-400/40" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Review not found</h2>
      <p className="text-white/40 mb-6 text-sm">This review may not be published yet or the link is incorrect.</p>
      <Link to="/reviews" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-semibold transition-colors">
        <SafeIcon icon={FiArrowLeft} /> Back to Reviews
      </Link>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      {/* Back link */}
      <Link 
        to="/reviews" 
        className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-8 transition-colors group"
      >
        <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
        Back to Reviews
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 xl:gap-12">
        {/* ── Main Content ── */}
        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="min-w-0"
        >
          {/* Disclosure */}
          <div className="mb-6 px-4 py-3 bg-yellow-500/5 border border-yellow-500/20 rounded-xl text-yellow-200/70 text-xs leading-relaxed">
            <strong className="text-yellow-300">Disclosure:</strong> This article contains affiliate links. If you make a purchase through these links, I may earn a commission at no extra cost to you.
          </div>

          {/* Hero Cover Image (With Gradient Fallback) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="w-full rounded-2xl overflow-hidden mb-8 border border-[#2a1f42] shadow-2xl shadow-purple-900/20"
            style={{ maxHeight: '480px' }}
          >
            {review.image_url ? (
              <img 
                src={review.image_url} 
                alt={review.title} 
                className="w-full h-full object-cover" 
                style={{ maxHeight: '480px', width: '100%' }} 
              />
            ) : (
              <div className="w-full h-64 sm:h-80 md:h-96 bg-gradient-to-br from-purple-600 via-pink-600 to-indigo-600 flex flex-col items-center justify-center p-6 sm:p-10 text-center" style={{ maxHeight: '480px' }}>
                <SafeIcon icon={FiStar} className="text-4xl sm:text-6xl text-white/20 mb-4" />
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-white drop-shadow-2xl tracking-tight max-w-3xl">
                  {review.title}
                </h2>
              </div>
            )}
          </motion.div>

          {/* Category + Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {review.category && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[11px] font-bold uppercase tracking-wide">
                <SafeIcon icon={FiZap} className="text-[10px]" />
                {review.category}
              </span>
            )}
            {(review.tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 text-[11px]">
                <SafeIcon icon={FiTag} className="text-[9px]" />
                {t}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-5 leading-tight tracking-tight">
            {review.title}
          </h1>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-3 mb-8 text-white/30 text-xs">
            <div className="flex items-center gap-1.5">
              <SafeIcon icon={FiCalendar} />
              <span>{new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <span>·</span>
            <span className="text-purple-300/60 font-semibold">{review.tool_name}</span>
          </div>

          {/* Rating + CTA Card */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-10 p-4 sm:p-5 bg-[#0a0118] border border-[#2a1f42] rounded-2xl">
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Our Rating</p>
              <div className="flex items-center gap-3">
                <StarDisplay value={review.rating} />
                <span className="text-white font-black text-3xl">{review.rating}</span>
                <span className="text-white/30 text-base">/5</span>
              </div>
            </div>
            
            {review.affiliate_link && (
              <a 
                href={review.affiliate_link} 
                target="_blank" 
                rel="noopener noreferrer sponsored"
                className="sm:ml-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 transition-all text-sm w-full sm:w-auto"
              >
                <SafeIcon icon={FiShoppingCart} /> Try This Tool <SafeIcon icon={FiExternalLink} className="text-xs opacity-70" />
              </a>
            )}
          </div>

          {/* Review Body */}
          <div 
            className="review-body"
            dangerouslySetInnerHTML={{ __html: review.body }}
          />

          {/* Bottom CTA */}
          {review.affiliate_link && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 p-6 sm:p-10 bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/30 rounded-2xl text-center"
            >
              <div className="flex justify-center mb-4">
                <StarDisplay value={review.rating} />
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-2">
                Ready to try {review.tool_name}?
              </h3>
              <p className="text-white/50 text-sm mb-6 max-w-md mx-auto leading-relaxed">
                Get access now through our affiliate link — same price, supports our work.
              </p>
              <a 
                href={review.affiliate_link} 
                target="_blank" 
                rel="noopener noreferrer sponsored"
                className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black text-base sm:text-lg rounded-2xl shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 transition-all"
              >
                <SafeIcon icon={FiShoppingCart} className="text-xl" /> Buy This Tool <SafeIcon icon={FiExternalLink} className="text-sm opacity-70" />
              </a>
              <p className="text-white/25 text-xs mt-4">Affiliate link — see disclosure above</p>
            </motion.div>
          )}

        </motion.article>

        {/* ── Sidebar ── */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          
          {/* Quick info */}
          <div className="bg-[#0a0118] border border-[#2a1f42] rounded-2xl p-5">
            <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-4 border-b border-white/5 pb-3">
              Quick Info
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/40 flex-shrink-0">Tool</span>
                <span className="text-white font-semibold text-right">{review.tool_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/40">Rating</span>
                <span className="text-yellow-400 font-black">{review.rating}/5 ★</span>
              </div>
              {review.category && (
                <div className="flex justify-between items-center">
                  <span className="text-white/40">Category</span>
                  <span className="text-purple-300 text-right">{review.category}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-white/40">Published</span>
                <span className="text-white/60 text-xs text-right">
                  {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>

            {review.affiliate_link && (
              <a 
                href={review.affiliate_link} 
                target="_blank" 
                rel="noopener noreferrer sponsored"
                className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 hover:scale-105 transition-all text-sm"
              >
                <SafeIcon icon={FiShoppingCart} /> Buy Tool
              </a>
            )}
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="bg-[#0a0118] border border-[#2a1f42] rounded-2xl p-5">
              <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-4 border-b border-white/5 pb-3">
                More Reviews
              </h3>
              <div className="space-y-3">
                {related.map(r => <RelatedCard key={r.id} review={r} />)}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ReviewDetail;